<template>
  <div class="sketch-offset-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">

      <!-- ========== 参数 ========== -->
      <n-collapse-item title="参数" name="params">
        <div class="param-list">

          <!-- 偏移距离 -->
          <div class="param-row">
            <span class="param-label">偏移距离</span>
            <n-input-group>
              <n-input-number
                :value="offsetDistance"
                size="small"
                :min="0.01"
                :step="1"
                placeholder="10"
                @update:value="handleDistanceChange"
              />
              <n-input-group-label size="small">mm</n-input-group-label>
            </n-input-group>
          </div>

          <!-- 添加尺寸 -->
          <n-checkbox
            :checked="addSize"
            @update:checked="handleAddSizeChange"
          >
            添加尺寸
          </n-checkbox>

          <!-- 反向（双向开启时不可选，与 SolidWorks 行为一致） -->
          <n-checkbox
            :checked="isReverse"
            :disabled="isBidirectional"
            @update:checked="handleReverseChange"
          >
            反向
          </n-checkbox>

          <!-- 选择链 -->
          <n-checkbox
            :checked="selectChain"
            @update:checked="handleSelectChainChange"
          >
            选择链
          </n-checkbox>

          <!-- 双向 -->
          <n-checkbox
            :checked="isBidirectional"
            @update:checked="handleBidirectionalChange"
          >
            双向
          </n-checkbox>

          <!-- ====== 盖子 ====== -->
          <n-checkbox
            :checked="coverHasCover"
            @update:checked="handleCoverHasCoverChange"
          >
            顶端加盖
          </n-checkbox>

          <!-- 盖子类型 — 次级 checkbox -->
          <div class="cover-type-row flex flex-col text-left">
            <n-checkbox
              :checked="coverType === 'circularArc'"
              @update:checked="handleCoverTypeArcChange"
            >
              圆弧
            </n-checkbox>
            <n-checkbox
              :checked="coverType === 'straight line'"
              @update:checked="handleCoverTypeLineChange"
            >
              直线
            </n-checkbox>
          </div>

          <!-- ====== 几何体选项 ====== -->
          <div class="font-bold">构造几何体:</div> 
          <n-checkbox
            :checked="basicGeometry"
            @update:checked="handleBasicGeometryChange"
          >
            基本几何体
          </n-checkbox>
          <n-checkbox
            :checked="offsetGeometry"
            @update:checked="handleOffsetGeometryChange"
          >
            偏移几何体
          </n-checkbox>

        </div>
      </n-collapse-item>

    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NInputGroup,
  NInputGroupLabel,
  NInputNumber,
} from 'naive-ui';
import type { PropertyManagerContext } from '@/core';
import type { CoverType } from '@puppycad/cad-core';

/**
 * 草图等距实体属性面板组件属性。
 */
interface SketchOffsetSectionProps {
  /** 属性面板上下文。 */
  context: PropertyManagerContext;
}

const props = defineProps<SketchOffsetSectionProps>();

/** 默认展开分组。 */
const defaultExpanded = ['params'];

// ═══════════════════════════════════════════════════════════
// 只读计算属性
// ═══════════════════════════════════════════════════════════

/** 当前偏移距离。 */
const offsetDistance = computed<number>(() => {
  return props.context.sketchOffsetDistance ?? 10;
});

/** 当前是否反向。 */
const isReverse = computed<boolean>(() => {
  return props.context.sketchOffsetReverse ?? false;
});

/** 当前是否双向。 */
const isBidirectional = computed<boolean>(() => {
  return props.context.sketchOffsetBidirectional ?? false;
});

/** 当前是否添加尺寸。 */
const addSize = computed<boolean>(() => {
  return props.context.sketchOffsetAddSize ?? false;
});

/** 当前是否开启选择链。 */
const selectChain = computed<boolean>(() => {
  return props.context.sketchOffsetSelectChain ?? true;
});

/** 当前盖子是否有盖。 */
const coverHasCover = computed<boolean>(() => {
  return props.context.sketchOffsetCover?.hasCover ?? false;
});

/** 当前盖子类型。 */
const coverType = computed<CoverType>(() => {
  return props.context.sketchOffsetCover?.type ?? 'circularArc';
});

/** 当前是否为基本几何体。 */
const basicGeometry = computed<boolean>(() => {
  return props.context.sketchOffsetBasicGeometry ?? false;
});

/** 当前是否为偏移几何体。 */
const offsetGeometry = computed<boolean>(() => {
  return props.context.sketchOffsetOffsetGeometry ?? false;
});

// ═══════════════════════════════════════════════════════════
// 事件处理
// ═══════════════════════════════════════════════════════════

/**
 * 处理距离变更。
 */
function handleDistanceChange(value: number | null): void {
  console.log('[等距实体] 偏移距离:', value);
  props.context.setSketchOffsetDistance?.(value ?? 10);
}

/**
 * 处理反向切换。
 */
function handleReverseChange(value: boolean): void {
  console.log('[等距实体] 反向:', value);
  props.context.setSketchOffsetReverse?.(value);
}

/**
 * 处理双向切换。
 */
function handleBidirectionalChange(value: boolean): void {
  console.log('[等距实体] 双向:', value);
  props.context.setSketchOffsetBidirectional?.(value);
}

/**
 * 处理添加尺寸切换。
 */
function handleAddSizeChange(value: boolean): void {
  console.log('[等距实体] 添加尺寸:', value);
  props.context.setSketchOffsetAddSize?.(value);
}

/**
 * 处理选择链切换。
 */
function handleSelectChainChange(value: boolean): void {
  console.log('[等距实体] 选择链:', value);
  props.context.setSketchOffsetSelectChain?.(value);
}

/**
 * 处理有盖切换。
 */
function handleCoverHasCoverChange(value: boolean): void {
  console.log('[等距实体] 顶端加盖:', value);
  props.context.setSketchOffsetCover?.({
    ...(props.context.sketchOffsetCover ?? { type: 'circularArc' }),
    hasCover: value,
  });
}

/**
 * 处理盖子类型切换为圆弧（互斥：选圆弧时取消直线）。
 */
function handleCoverTypeArcChange(checked: boolean): void {
  if (!checked) return; // 不允许取消最后一个选中的类型，保持至少一个选中
  console.log('[等距实体] 盖子类型-圆弧:', checked);
  props.context.setSketchOffsetCover?.({
    ...(props.context.sketchOffsetCover ?? { hasCover: false }),
    type: 'circularArc' as CoverType,
  });
}

/**
 * 处理盖子类型切换为直线（互斥：选直线时取消圆弧）。
 */
function handleCoverTypeLineChange(checked: boolean): void {
  if (!checked) return;
  console.log('[等距实体] 盖子类型-直线:', checked);
  props.context.setSketchOffsetCover?.({
    ...(props.context.sketchOffsetCover ?? { hasCover: false }),
    type: 'straight line' as CoverType,
  });
}

/**
 * 处理基本几何体切换。
 */
function handleBasicGeometryChange(value: boolean): void {
  console.log('[等距实体] 基本几何体:', value);
  if (!props.context.setSketchOffsetBasicGeometry) {
    // 可定位日志：context 缺失 setter 说明运行时模块版本不一致（新旧模块混用），
    // 勾选会静默失效，必须提示以便定位
    console.warn('[等距实体] context 缺少 setSketchOffsetBasicGeometry，勾选无法生效，请刷新页面重新加载。');
    return;
  }
  props.context.setSketchOffsetBasicGeometry(value);
}

/**
 * 处理偏移几何体切换。
 */
function handleOffsetGeometryChange(value: boolean): void {
  console.log('[等距实体] 偏移几何体:', value);
  if (!props.context.setSketchOffsetOffsetGeometry) {
    // 可定位日志：context 缺失 setter 说明运行时模块版本不一致（新旧模块混用），
    // 勾选会静默失效，必须提示以便定位
    console.warn('[等距实体] context 缺少 setSketchOffsetOffsetGeometry，勾选无法生效，请刷新页面重新加载。');
    return;
  }
  props.context.setSketchOffsetOffsetGeometry(value);
}
</script>

<style scoped>
.sketch-offset-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.param-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.param-label {
  flex-shrink: 0;
  width: 60px;
  font-size: 13px;
  color: var(--n-text-color);
}

.param-row .n-input-group {
  flex: 1;
}

/* 盖子类型次级 checkbox 行：垂直排列，左对齐，缩进 */
.cover-type-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding-left: 24px;
}
</style>
